import { type FC } from "react";

import { clsx } from "clsx";

import { type IconProp } from "@fortawesome/fontawesome-svg-core";
import { faArrowsRotate } from "@fortawesome/pro-duotone-svg-icons/faArrowsRotate";
import { faClipboardList } from "@fortawesome/pro-duotone-svg-icons/faClipboardList";
import { faFolderOpen } from "@fortawesome/pro-duotone-svg-icons/faFolderOpen";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { EvaluationTypeEnum } from "@app_schema";

type EvalTypeOption = {
  value: EvaluationTypeEnum;
  icon: IconProp;
  title: string;
  description: string;
};

const EVAL_TYPE_OPTIONS: ReadonlyArray<EvalTypeOption> = [
  {
    value: EvaluationTypeEnum.Comprehensive,
    icon: faClipboardList,
    title: "Comprehensive Evaluation",
    description:
      "First evaluation for this patient. Upload your medical records and we'll organize them into a chronological summary.",
  },
  {
    value: EvaluationTypeEnum.Supplemental,
    icon: faFolderOpen,
    title: "Supplemental Report",
    description:
      "Records received after the initial evaluation. Upload and we'll organize them for your review.",
  },
  {
    value: EvaluationTypeEnum.Followup,
    icon: faArrowsRotate,
    title: "Follow-Up Evaluation",
    description:
      "Re-evaluation with new records. Upload the prior records and new records — we'll flag what was previously reviewed and highlight what's new in your summary.",
  },
];

/**
 * Screen A1 `.eval-choice` cards per design handoff Part 2 — the initial
 * "what kind of evaluation is this?" picker that owns the idle display state.
 *
 * Distinct from the smaller `.choice-card` used by the Add Records wizard
 * (see `choice_card.tsx`). These cards are bigger (24px padding, 32px icon,
 * 16px title) to anchor the idle-state hero.
 */
export const EvalTypeChoices: FC<{
  selected: EvaluationTypeEnum;
  onSelect: (type: EvaluationTypeEnum) => void;
  loading?: boolean;
}> = ({ selected, onSelect, loading }) => (
  <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
    {EVAL_TYPE_OPTIONS.map((option) => (
      <EvaluationTypeChoice
        key={option.value}
        icon={option.icon}
        title={option.title}
        description={option.description}
        selected={selected === option.value}
        disabled={loading}
        onClick={() => onSelect(option.value)}
      />
    ))}
  </div>
);

// D5: when the parent's mutation is in flight, `disabled` disables pointer
// events on both cards but does NOT dim them — the optimistic selected state
// continues to display so the user gets immediate feedback.
const EvaluationTypeChoice: FC<{
  icon: IconProp;
  title: string;
  description: string;
  selected: boolean;
  disabled?: boolean;
  onClick: () => void;
}> = ({ icon, title, description, selected, disabled, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={clsx(
      "relative cursor-pointer rounded-[18px] p-6 text-left transition-all motion-reduce:transition-none",
      selected ? "surface-lit" : "surface-resting hover:-translate-y-px",
      "disabled:pointer-events-none disabled:cursor-default",
    )}
  >
    <span
      aria-hidden="true"
      className={clsx(
        "absolute top-4 right-4 flex size-[22px] items-center justify-center rounded-full border-2",
        selected ? "border-indigo-500 bg-indigo-500" : "border-slate-300",
      )}
    >
      {selected && (
        <span className="size-[8px] rounded-full border-[3px] border-white" />
      )}
    </span>
    <div
      className="mb-3 text-[32px] leading-none text-indigo-600"
      aria-hidden="true"
    >
      <FontAwesomeIcon icon={icon} />
    </div>
    <div className="text-ink mb-1.5 text-base font-semibold">{title}</div>
    <div className="text-muted text-[13px] leading-normal">{description}</div>
    <div className="text-muted mt-2.5 text-[11px] leading-normal">
      You can add more records at any time.
    </div>
  </button>
);
